import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Calendar, Clock, CreditCard, CheckCircle, User, Mail, Phone, Globe, UserPlus, AlertTriangle } from 'lucide-react';
import CrisisDisclaimer from '../components/CrisisDisclaimer';
import CalendlyStyleCalendar from '../components/CalendlyStyleCalendar';
import { getLocalTimezone, convertFromIST, formatTimeDisplay, getTimezoneAbbreviation } from '../utils/timezone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CALENDLY_LINKS = {
  'Individual Therapy': 'https://calendly.com/therapywithanvitasethi/psychotherapy-session',
  'Couples Therapy': 'https://calendly.com/therapywithanvitasethi/couplestherapy-session-international-clients',
};

const BookSession = () => {
  const [step, setStep] = useState(1);
  const [location, setLocation] = useState('');
  const [locationDetected, setLocationDetected] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(true);
  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    session_date: '',
    session_time: '',
    duration: 60,
    notes: '',
    additional_participant: {
      name: '',
      email: '',
      relationship: ''
    }
  });
  const [sessionId, setSessionId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [allAvailableSlots, setAllAvailableSlots] = useState([]); // All slots for calendar
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [prices, setPrices] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [clientTimezone, setClientTimezone] = useState('Asia/Kolkata');
  const [timezoneAbbr, setTimezoneAbbr] = useState('IST');
  const [bookingUnavailable, setBookingUnavailable] = useState(null);

  // Check booking availability + fetch prices on mount
  useEffect(() => {
    checkBookingAvailability();
    fetchPrices();
    detectUserLocation();
    const detectedTz = getLocalTimezone();
    setClientTimezone(detectedTz);
    setTimezoneAbbr(getTimezoneAbbreviation(detectedTz));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkBookingAvailability = async () => {
    try {
      const res = await axios.get(`${API}/booking-status`);
      if (!res.data.available) {
        setBookingUnavailable(res.data.message);
      }
    } catch (err) {
      console.error('Booking availability check failed:', err);
    }
  };

  const detectUserLocation = async () => {
    setDetectingLocation(true);
    
    // Check if geolocation is available
    if (!navigator.geolocation) {
      console.log('Geolocation not supported');
      setDetectingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          // Use reverse geocoding to determine country
          // Using a free API - nominatim from OpenStreetMap
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=en`
          );
          const data = await response.json();
          
          const country = data.address?.country || data.address?.country_code;
          const isIndia = country?.toLowerCase() === 'india' || data.address?.country_code?.toLowerCase() === 'in';
          
          setLocation(isIndia ? 'india' : 'international');
          setLocationDetected(true);
          setDetectingLocation(false);
        } catch (error) {
          console.error('Error detecting location:', error);
          setDetectingLocation(false);
        }
      },
      (error) => {
        console.log('Geolocation error:', error.message);
        setDetectingLocation(false);
        // Don't show error toast - user may have denied permission intentionally
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000 // Cache for 5 minutes
      }
    );
  };

  // Fetch prices on component mount
  useEffect(() => {
    fetchPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPrices = async () => {
    try {
      const response = await axios.get(`${API}/prices`);
      setPrices(response.data);
    } catch (error) {
      console.error('Error fetching prices:', error);
    }
  };

  const servicesIndia = prices.map(p => ({
    name: p.service_name,
    price: p.price_inr,
    duration: p.duration,
    currency: 'INR',
    slot_type: p.service_name.includes('Couples') ? 'couples' : 'individual'
  }));

  const servicesInternational = prices
    .filter(p => p.service_name !== 'Supervision')
    .map(p => {
      const calendlyUrl = CALENDLY_LINKS[p.service_name] || null;
      return {
        name: `${p.service_name} (International)`,
        price: p.price_usd,
        currency: 'USD',
        duration: p.duration,
        slot_type: p.service_name.includes('Couples') ? 'couples' : 'individual',
        calendlyUrl,
        comingSoon: !calendlyUrl
      };
    });

  // For international users, also show Supervision as INR-only option
  const supervisionPrice = prices.find(p => p.service_name === 'Supervision');
  if (location === 'international' && supervisionPrice) {
    servicesInternational.push({
      name: 'Supervision',
      price: supervisionPrice.price_inr,
      currency: 'INR',
      duration: supervisionPrice.duration,
      slot_type: 'individual'
    });
  }

  const services = location === 'india' ? servicesIndia : servicesInternational;
  const selectedService = services.find(s => s.name === bookingData.service_type);

  // Fetch available dates when location is selected
  useEffect(() => {
    if (location) {
      fetchAvailableDates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Fetch available slots when date changes
  useEffect(() => {
    if (bookingData.session_date && bookingData.service_type) {
      fetchAvailableSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingData.session_date, bookingData.service_type]);

  const fetchAvailableDates = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const threeMonthsLater = new Date();
      threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
      const endDate = threeMonthsLater.toISOString().split('T')[0];

      const response = await axios.get(`${API}/slots/available-dates`, {
        params: { from_date: today, to_date: endDate }
      });
      setAvailableDates(response.data.available_dates || []);
    } catch (error) {
      console.error('Error fetching available dates:', error);
    }
  };

  // Fetch all available slots for the selected service type
  const fetchAllAvailableSlots = async (slotType) => {
    setLoadingSlots(true);
    try {
      // Use the optimized endpoint that returns all slots in one request
      const response = await axios.get(`${API}/slots/all-available`, {
        params: { slot_type: slotType, limit: 50 }
      });
      
      const slots = response.data || [];
      
      // Convert times from IST to client timezone
      const slotsWithLocalTime = slots.map(slot => {
        const converted = convertFromIST(slot.date, slot.time, clientTimezone);
        return {
          ...slot,
          time_ist: slot.time,
          time_local: converted.time,
          display_date: converted.date,
          date_changed: converted.dateChanged
        };
      });
      
      setAllAvailableSlots(slotsWithLocalTime);
    } catch (error) {
      console.error('Error fetching all slots:', error);
      toast.error('Failed to load available slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  const fetchAvailableSlots = async () => {
    try {
      const response = await axios.get(`${API}/slots/available`, {
        params: { 
          date: bookingData.session_date,
          slot_type: selectedService.slot_type
        }
      });
      // Convert slot times from IST to client timezone
      const slotsWithLocalTime = (response.data || []).map(slot => {
        const converted = convertFromIST(bookingData.session_date, slot.time, clientTimezone);
        return {
          ...slot,
          time_ist: slot.time, // Keep original IST time for booking
          time: converted.time, // Display time in local timezone
          display_date: converted.date,
          date_changed: converted.dateChanged
        };
      });
      setAvailableSlots(slotsWithLocalTime);
    } catch (error) {
      toast.error('Failed to load available slots');
    }
  };

  const handleNext = async () => {
    if (step === 1 && !location) {
      toast.error('Please select your location');
      return;
    }

    if (step === 2 && !bookingData.service_type) {
      toast.error('Please select a service');
      return;
    }
    
    // International with Calendly — skip to Calendly embed
    if (step === 2 && location === 'international' && selectedService?.calendlyUrl) {
      setStep(3);
      return;
    }
    
    // When moving from step 2 to 3 (India flow), fetch all available slots
    if (step === 2 && bookingData.service_type) {
      const slotType = bookingData.service_type.toLowerCase().includes('couple') ? 'couples' : 'individual';
      fetchAllAvailableSlots(slotType);
    }
    
    if (step === 3) {
      if (!bookingData.name || !bookingData.email || !bookingData.phone) {
        toast.error('Please fill in all required fields');
        return;
      }
      if (!bookingData.session_date || !bookingData.session_time) {
        toast.error('Please select date and time');
        return;
      }
      
      // Validate booking (but don't create it yet)
      setLoading(true);
      try {
        const response = await axios.post(`${API}/bookings/validate`, {
          ...bookingData,
          name: bookingData.name,
          email: bookingData.email,
          phone: bookingData.phone
        });
        
        if (response.data.valid) {
          toast.success('Booking details validated');
          setPaymentMethod('razorpay');
          setStep(step + 1);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Validation failed');
      } finally {
        setLoading(false);
      }
    } else {
      setStep(step + 1);
    }
  };

  const handlePayment = async () => {
    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    setLoading(true);
    try {
      let amount = selectedService?.price;
      let currency = location === 'international' ? 'USD' : 'INR';
      
      // Create Razorpay order with booking data
      const orderResponse = await axios.post(`${API}/payments/razorpay/create-order`, {
        amount: amount,
        currency: currency,
        booking_data: {
          ...bookingData,
          original_currency: currency,
          original_amount: selectedService?.price,
          converted_amount: amount
        }
      });

        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_key',
          amount: orderResponse.data.amount,
          currency: orderResponse.data.currency,
          order_id: orderResponse.data.id,
          name: 'Psychotherapy with Anvita',
          description: bookingData.service_type?.replace(' (International)', ''),
          handler: async function (response) {
            try {
              // Verify payment and create booking
              const verifyResponse = await axios.post(`${API}/payments/razorpay/verify`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                temp_id: orderResponse.data.temp_id
              });
              
              setSessionId(verifyResponse.data.session_id);
              toast.success('Payment successful! Booking confirmed.');
              setStep(5);
            } catch (error) {
              toast.error('Payment verification failed. Please contact support.');
            }
          },
          prefill: {
            name: bookingData.name,
            email: bookingData.email,
            contact: bookingData.phone
          },
          theme: { color: '#4A6741' },
          modal: {
            ondismiss: function() {
              setLoading(false);
            }
          }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 px-6 py-16" data-testid="book-session-page">
      <div className="max-w-4xl mx-auto">
        {/* Booking Unavailable Banner */}
        {bookingUnavailable && (
          <div className="mb-8 p-6 bg-red-50 border-2 border-red-200 rounded-2xl text-center" data-testid="booking-unavailable-banner">
            <AlertTriangle size={32} className="mx-auto text-red-500 mb-3" />
            <h2 className="text-xl font-heading font-medium text-red-800 mb-2">Booking Temporarily Unavailable</h2>
            <p className="text-red-600">{bookingUnavailable}</p>
          </div>
        )}

        {!bookingUnavailable && (
          <>
        {/* Progress Steps */}
        <div className="flex justify-between mb-12">
          {(location === 'international' && selectedService?.calendlyUrl
            ? ['Location', 'Select Service', 'Book Session']
            : ['Location', 'Select Service', 'Your Details & Time', 'Payment', 'Confirmation']
          ).map((label, idx) => (
            <div key={label} className="flex flex-col items-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                step > idx + 1 ? 'bg-primary text-white' : step === idx + 1 ? 'bg-accent text-white' : 'bg-muted text-muted-foreground'
              }`}>
                {step > idx + 1 ? <CheckCircle size={20} /> : idx + 1}
              </div>
              <p className="text-sm mt-2 text-center text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>

        {/* Step 1: Location Selection */}
        {step === 1 && (
          <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8" data-testid="location-selection-step">
            {/* Show loading while detecting */}
            {detectingLocation && (
              <>
                <h2 className="text-3xl font-heading font-medium text-foreground mb-4">Detecting your location...</h2>
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-3 border-primary border-t-transparent rounded-full"></div>
                </div>
                <p className="text-center text-muted-foreground">This helps us show you the correct pricing</p>
              </>
            )}
            
            {/* Location detected - show confirmation and proceed */}
            {!detectingLocation && locationDetected && location && (
              <>
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Globe size={32} className="text-primary" />
                  </div>
                  <h2 className="text-3xl font-heading font-medium text-foreground mb-2">
                    {location === 'india' ? 'Booking from India' : 'International Booking'}
                  </h2>
                  <p className="text-lg text-muted-foreground mb-2">
                    Sessions will be priced in {location === 'india' ? 'INR (₹)' : 'USD ($)'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Based on your current location
                  </p>
                </div>
                
                <Button
                  onClick={handleNext}
                  className="w-full bg-primary text-white hover:bg-primary/90 rounded-full py-6 text-lg"
                  data-testid="next-step-button"
                >
                  Continue to Select Service
                </Button>
                
                {/* Hidden option - only visible if user scrolls/looks for it */}
                <div className="mt-12 pt-6 border-t border-muted/30">
                  <details className="text-sm">
                    <summary className="text-muted-foreground/60 cursor-pointer hover:text-muted-foreground text-xs">
                      Location incorrect?
                    </summary>
                    <div className="mt-4 p-4 bg-secondary/30 rounded-xl">
                      <p className="text-xs text-muted-foreground mb-3">
                        If you're currently in India and seeing international pricing, you can update your location below:
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => { setLocation('india'); }}
                          className={`p-3 rounded-lg border text-sm transition-all ${
                            location === 'india' 
                              ? 'border-primary bg-primary/5 text-primary' 
                              : 'border-muted text-muted-foreground hover:border-primary/30'
                          }`}
                          data-testid="hidden-location-india"
                        >
                          I'm in India
                        </button>
                        <button
                          onClick={() => { setLocation('international'); }}
                          className={`p-3 rounded-lg border text-sm transition-all ${
                            location === 'international' 
                              ? 'border-primary bg-primary/5 text-primary' 
                              : 'border-muted text-muted-foreground hover:border-primary/30'
                          }`}
                          data-testid="hidden-location-international"
                        >
                          I'm outside India
                        </button>
                      </div>
                    </div>
                  </details>
                </div>
              </>
            )}
            
            {/* Fallback if geolocation fails or denied - show manual selection */}
            {!detectingLocation && !locationDetected && (
              <>
                <h2 className="text-3xl font-heading font-medium text-foreground mb-2">Where are you booking from?</h2>
                <p className="text-muted-foreground mb-8">
                  Please select your current location to see the appropriate pricing.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div
                    onClick={() => { setLocation('india'); setLocationDetected(true); }}
                    className={`p-8 rounded-2xl border-2 cursor-pointer transition-all ${
                      location === 'india' ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
                    }`}
                    data-testid="location-india"
                  >
                    <Globe size={32} className="text-primary mb-4" />
                    <h3 className="text-2xl font-heading font-medium text-foreground mb-2">India</h3>
                    <p className="text-muted-foreground">Sessions priced in INR (₹)</p>
                  </div>
                  <div
                    onClick={() => { setLocation('international'); setLocationDetected(true); }}
                    className={`p-8 rounded-2xl border-2 cursor-pointer transition-all ${
                      location === 'international' ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
                    }`}
                    data-testid="location-international"
                  >
                    <Globe size={32} className="text-primary mb-4" />
                    <h3 className="text-2xl font-heading font-medium text-foreground mb-2">International</h3>
                    <p className="text-muted-foreground">Sessions priced in USD ($)</p>
                  </div>
                </div>
                
                <div className="mt-8">
                  <Button
                    onClick={handleNext}
                    disabled={!location}
                    className="w-full bg-primary text-white hover:bg-primary/90 rounded-full py-6 text-lg disabled:opacity-50"
                    data-testid="next-step-button"
                  >
                    Continue
                  </Button>
                </div>
              </>
            )}
          </Card>
        )}

        {/* Step 2: Select Service */}
        {step === 2 && (
          <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8" data-testid="service-selection-step">
            <h2 className="text-3xl font-heading font-medium text-foreground mb-8">Select Your Service</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map((service) => (
                <div
                  key={service.name}
                  onClick={() => {
                    if (!service.comingSoon) {
                      setBookingData({ ...bookingData, service_type: service.name, duration: service.duration });
                    }
                  }}
                  className={`p-6 rounded-2xl border-2 transition-all ${
                    service.comingSoon
                      ? 'border-muted bg-muted/20 cursor-not-allowed opacity-60'
                      : bookingData.service_type === service.name
                        ? 'border-primary bg-primary/5 cursor-pointer'
                        : 'border-muted hover:border-primary/50 cursor-pointer'
                  }`}
                  data-testid={`service-option-${service.name.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-heading font-medium text-foreground">{service.name.replace(' (International)', '')}</h3>
                    {service.comingSoon && (
                      <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">Coming Soon</span>
                    )}
                  </div>
                  <p className="text-2xl font-bold text-accent mb-1">
                    {service.currency === 'USD' ? `$${service.price}` : `₹${service.price}`}
                  </p>
                  <p className="text-sm text-muted-foreground">{service.duration} minutes</p>
                  {service.comingSoon && (
                    <p className="text-xs text-muted-foreground mt-2">This service will be available for international clients soon.</p>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-8">
              <Button onClick={() => setStep(1)} variant="outline" className="flex-1 rounded-full py-6">
                Back
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 bg-primary text-white hover:bg-primary/90 rounded-full py-6"
                data-testid="next-step-button-2"
              >
                Continue
              </Button>
            </div>
          </Card>
        )}

        {/* Step 3: Calendly Embed for International OR Details & Date/Time for India */}
        {step === 3 && location === 'international' && selectedService?.calendlyUrl && (
          <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8" data-testid="calendly-embed-step">
            <h2 className="text-3xl font-heading font-medium text-foreground mb-2">
              Book Your Session
            </h2>
            <p className="text-muted-foreground mb-6">
              Select a convenient time below. Payment will be handled securely through the scheduling page.
            </p>
            <div className="rounded-2xl overflow-hidden border border-muted" style={{ minHeight: '700px' }}>
              <iframe
                src={selectedService.calendlyUrl}
                width="100%"
                height="700"
                frameBorder="0"
                title="Schedule a session"
                data-testid="calendly-iframe"
                style={{ border: 'none' }}
              />
            </div>
            <div className="mt-6">
              <Button onClick={() => setStep(2)} variant="outline" className="rounded-full py-6 px-8">
                Back to Services
              </Button>
            </div>
          </Card>
        )}

        {step === 3 && !(location === 'international' && selectedService?.calendlyUrl) && (
          <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8" data-testid="details-datetime-step">
            <h2 className="text-3xl font-heading font-medium text-foreground mb-8">Your Details & Session Time</h2>
            <div className="space-y-6">
              <div>
                <Label className="text-foreground font-medium flex items-center gap-2">
                  <User size={20} /> Full Name *
                </Label>
                <Input
                  type="text"
                  value={bookingData.name}
                  onChange={(e) => setBookingData({ ...bookingData, name: e.target.value })}
                  className="mt-2 bg-white/50 border-muted rounded-lg"
                  placeholder="Enter your full name"
                  data-testid="name-input"
                  required
                />
              </div>
              
              <div>
                <Label className="text-foreground font-medium flex items-center gap-2">
                  <Mail size={20} /> Email *
                </Label>
                <Input
                  type="email"
                  value={bookingData.email}
                  onChange={(e) => setBookingData({ ...bookingData, email: e.target.value })}
                  className="mt-2 bg-white/50 border-muted rounded-lg"
                  placeholder="your@email.com"
                  data-testid="email-input"
                  required
                />
              </div>
              
              <div>
                <Label className="text-foreground font-medium flex items-center gap-2">
                  <Phone size={20} /> Phone Number *
                </Label>
                <Input
                  type="tel"
                  value={bookingData.phone}
                  onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
                  className="mt-2 bg-white/50 border-muted rounded-lg"
                  placeholder="+91 98765 43210"
                  data-testid="phone-input"
                  required
                />
              </div>

              {/* Additional Participant - Only for Couples and Adolescent Therapy */}
              {(bookingData.service_type?.toLowerCase().includes('couple') || 
                bookingData.service_type?.toLowerCase().includes('adolescent')) && (
                <div className="border border-primary/20 bg-primary/5 rounded-2xl p-6 mt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <UserPlus size={20} className="text-primary" />
                    <h3 className="text-lg font-heading font-medium text-foreground">
                      {bookingData.service_type?.toLowerCase().includes('couple') 
                        ? 'Partner Details' 
                        : 'Parent/Guardian Details'}
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    {bookingData.service_type?.toLowerCase().includes('couple')
                      ? 'Please provide details of your partner who will be joining the session.'
                      : 'Please provide details of the parent/guardian who will be involved in the therapy process.'}
                  </p>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-foreground font-medium">
                        {bookingData.service_type?.toLowerCase().includes('couple') ? "Partner's Name *" : "Parent/Guardian Name *"}
                      </Label>
                      <Input
                        type="text"
                        value={bookingData.additional_participant?.name || ''}
                        onChange={(e) => setBookingData({ 
                          ...bookingData, 
                          additional_participant: { ...bookingData.additional_participant, name: e.target.value }
                        })}
                        className="mt-2 bg-white/50 border-muted rounded-lg"
                        placeholder="Enter full name"
                        data-testid="additional-participant-name"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-foreground font-medium">
                        {bookingData.service_type?.toLowerCase().includes('couple') ? "Partner's Email *" : "Parent/Guardian Email *"}
                      </Label>
                      <Input
                        type="email"
                        value={bookingData.additional_participant?.email || ''}
                        onChange={(e) => setBookingData({ 
                          ...bookingData, 
                          additional_participant: { ...bookingData.additional_participant, email: e.target.value }
                        })}
                        className="mt-2 bg-white/50 border-muted rounded-lg"
                        placeholder="partner@email.com"
                        data-testid="additional-participant-email"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-foreground font-medium">Relationship</Label>
                      <Select 
                        value={bookingData.additional_participant?.relationship || ''} 
                        onValueChange={(value) => setBookingData({ 
                          ...bookingData, 
                          additional_participant: { ...bookingData.additional_participant, relationship: value }
                        })}
                      >
                        <SelectTrigger className="mt-2 bg-white/50 border-muted rounded-lg" data-testid="relationship-select">
                          <SelectValue placeholder="Select relationship" />
                        </SelectTrigger>
                        <SelectContent>
                          {bookingData.service_type?.toLowerCase().includes('couple') ? (
                            <>
                              <SelectItem value="spouse">Spouse</SelectItem>
                              <SelectItem value="partner">Partner</SelectItem>
                              <SelectItem value="fiance">Fiancé/Fiancée</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="mother">Mother</SelectItem>
                              <SelectItem value="father">Father</SelectItem>
                              <SelectItem value="guardian">Legal Guardian</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              <div className="border-t border-muted pt-6 mt-6">
                <h3 className="text-xl font-heading font-medium text-foreground mb-2">Select Date & Time</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Pick a date to see available times
                  {clientTimezone !== 'Asia/Kolkata' && (
                    <span> (times shown in {timezoneAbbr})</span>
                  )}
                </p>
                
                {loadingSlots ? (
                  <div className="text-center py-12 bg-secondary/30 rounded-2xl">
                    <div className="animate-spin w-8 h-8 border-3 border-primary border-t-transparent rounded-full mx-auto"></div>
                    <p className="mt-4 text-muted-foreground">Loading available slots...</p>
                  </div>
                ) : allAvailableSlots.length === 0 ? (
                  <div className="text-center py-12 bg-secondary/30 rounded-2xl">
                    <Calendar size={48} className="mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No available slots in the next 3 months.</p>
                    <p className="text-sm text-muted-foreground mt-2">Please check back later or contact us directly.</p>
                  </div>
                ) : (
                  <CalendlyStyleCalendar
                    availableSlots={allAvailableSlots}
                    selectedDate={bookingData.session_date}
                    selectedTime={bookingData.session_time}
                    onSelectSlot={(date, time) => setBookingData({ 
                      ...bookingData, 
                      session_date: date, 
                      session_time: time 
                    })}
                    clientTimezone={clientTimezone}
                    timezoneAbbr={timezoneAbbr}
                  />
                )}
                
                {bookingData.session_date && bookingData.session_time && (
                  <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-xl">
                    <p className="text-sm font-medium text-foreground flex items-center gap-2">
                      <CheckCircle size={18} className="text-primary" />
                      Selected: {new Date(bookingData.session_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} at {formatTimeDisplay(allAvailableSlots.find(s => s.date === bookingData.session_date && s.time_ist === bookingData.session_time)?.time_local || bookingData.session_time)} {timezoneAbbr}
                    </p>
                  </div>
                )}
              </div>
              
              <div>
                <Label className="text-foreground font-medium">Additional Notes (Optional)</Label>
                <Textarea
                  value={bookingData.notes}
                  onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                  className="mt-2 bg-white/50 border-muted rounded-lg"
                  placeholder="Any specific concerns or topics you'd like to discuss?"
                  rows={4}
                  data-testid="notes-textarea"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-8">
              <Button onClick={() => setStep(2)} variant="outline" className="flex-1 rounded-full py-6" data-testid="back-button">
                Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={loading}
                className="flex-1 bg-primary text-white hover:bg-primary/90 rounded-full py-6"
                data-testid="continue-payment-button"
              >
                {loading ? 'Saving...' : 'Continue to Payment'}
              </Button>
            </div>
          </Card>
        )}

        {/* Step 4: Payment */}
        {step === 4 && (
          <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8" data-testid="payment-step">
            <h2 className="text-3xl font-heading font-medium text-foreground mb-8">Complete Payment</h2>
            
            {/* Order Summary */}
            <div className="bg-secondary/50 rounded-2xl p-6 mb-8">
              <div className="flex justify-between mb-2">
                <span className="text-muted-foreground">Service:</span>
                <span className="font-medium text-foreground">{bookingData.service_type?.replace(' (International)', '')}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-muted-foreground">Date:</span>
                <span className="font-medium text-foreground">{bookingData.session_date}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-muted-foreground">Time:</span>
                <span className="font-medium text-foreground">
                  {formatTimeDisplay(convertFromIST(bookingData.session_date, bookingData.session_time, clientTimezone).time)} {timezoneAbbr}
                </span>
              </div>
              <div className="border-t border-muted mt-4 pt-4 flex justify-between">
                <span className="text-foreground font-medium">Total Amount:</span>
                <div className="text-right">
                  <span className="text-2xl font-bold text-accent">
                    {location === 'india' ? `₹${selectedService?.price}` : `$${selectedService?.price}`}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment via Razorpay */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard size={20} className="text-primary" />
                <Label className="text-foreground font-medium">Payment via Razorpay</Label>
              </div>
              <div
                className="p-6 rounded-2xl border-2 border-primary bg-primary/5"
                data-testid="payment-razorpay"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-foreground">
                      {location === 'india' ? `Pay ₹${selectedService?.price}` : `Pay $${selectedService?.price} USD`}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {location === 'india' 
                        ? 'UPI, Cards, Net Banking, Wallets' 
                        : 'Cards, PayPal & international payment methods'}
                    </p>
                  </div>
                  <CheckCircle size={24} className="text-primary" />
                </div>
              </div>
            </div>

            {/* Terms and Conditions Checkbox */}
            <div className="flex items-start gap-3 mb-8 p-4 bg-secondary/30 rounded-xl">
              <Checkbox
                id="terms"
                checked={termsAccepted}
                onCheckedChange={setTermsAccepted}
                className="mt-1"
                data-testid="terms-checkbox"
              />
              <Label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                I have read and agree to the{' '}
                <Link to="/terms" target="_blank" className="text-primary hover:underline font-medium">
                  Terms of Use
                </Link>
                ,{' '}
                <Link to="/privacy" target="_blank" className="text-primary hover:underline font-medium">
                  Privacy Policy
                </Link>
                , and{' '}
                <Link to="/refund-policy" target="_blank" className="text-primary hover:underline font-medium">
                  Refund & Cancellation Policy
                </Link>
                .
              </Label>
            </div>

            <div className="flex gap-4">
              <Button onClick={() => setStep(3)} variant="outline" className="flex-1 rounded-full py-6" data-testid="back-to-details-button">
                Back
              </Button>
              <Button
                onClick={handlePayment}
                disabled={loading || !paymentMethod || !termsAccepted}
                className="flex-1 bg-accent text-white hover:bg-accent/90 rounded-full py-6 disabled:opacity-50"
                data-testid="proceed-payment-button"
              >
                {loading ? 'Processing...' : `Pay ${
                  location === 'india' 
                    ? `₹${selectedService?.price}` 
                    : `$${selectedService?.price}`
                }`}
              </Button>
            </div>
          </Card>
        )}

        {/* Step 5: Confirmation */}
        {step === 5 && (
          <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8" data-testid="confirmation-step">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={48} className="text-primary" />
              </div>
              <h2 className="text-3xl font-heading font-medium text-foreground mb-4">Booking Confirmed!</h2>
              <p className="text-lg text-muted-foreground mb-4">
                Your therapy session has been successfully booked and payment confirmed.
              </p>
              <p className="text-base text-muted-foreground">
                You will receive a confirmation email shortly with all the details and a Google Calendar invite.
              </p>
            </div>

            {/* Session Details */}
            <div className="bg-secondary/50 rounded-2xl p-6 mb-6 text-left">
              <h3 className="font-medium text-foreground mb-4">Session Details:</h3>
              <div className="space-y-2 text-muted-foreground">
                <p><span className="font-medium text-foreground">Name:</span> {bookingData.name}</p>
                <p><span className="font-medium text-foreground">Service:</span> {bookingData.service_type}</p>
                <p><span className="font-medium text-foreground">Date:</span> {bookingData.session_date}</p>
                <p>
                  <span className="font-medium text-foreground">Time:</span>{' '}
                  {formatTimeDisplay(convertFromIST(bookingData.session_date, bookingData.session_time, clientTimezone).time)} {timezoneAbbr}
                </p>
                <p><span className="font-medium text-foreground">Duration:</span> {bookingData.duration} minutes</p>
              </div>
            </div>

            {/* Preparation Notes */}
            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 mb-6 text-left">
              <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" x2="8" y1="13" y2="13"/><line x1="16" x2="8" y1="17" y2="17"/><line x1="10" x2="8" y1="9" y2="9"/></svg>
                Before Your Session
              </h3>
              <p className="text-sm text-muted-foreground mb-3">Here are a few things to remember:</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Please ensure you're in a quiet and private space where no one will disturb you for an hour</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Make sure your internet connection is strong</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>During the session, keep a bottle of water, a notebook, and a pen with you</span>
                </li>
              </ul>
            </div>

            {/* Intake Form - for first-time clients */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6 text-left">
              <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/></svg>
                First Session? Please Complete the Intake Form
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                If this is your first session, please fill out the intake form before we meet. This helps me understand your needs better and make the most of our time together.
              </p>
              <a 
                href="https://docs.google.com/forms/d/e/1FAIpQLSccQdWvf4Vi7SYZh_2ijAZPKB0qsUY58NXGlwX0gc2VTK_hwA/viewform"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-amber-700 transition-colors"
                data-testid="intake-form-link"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/></svg>
                Complete Intake Form
              </a>
            </div>

            {/* Cancellation & Reschedule Policy */}
            <div className="bg-accent/10 border border-accent/20 rounded-2xl p-6 mb-8 text-left">
              <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>
                Reschedule or Cancellation Policy
              </h3>
              <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-1">•</span>
                  <span>If you need to cancel or reschedule, please do it at least 24 hours before the session</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-1">•</span>
                  <span>For example, if our session is on Saturday at 11:00 am, please let me know before Friday at 11:00 am</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-1">•</span>
                  <span>Cancellations within 24 hours of session timings are non-refundable</span>
                </li>
              </ul>
              <p className="text-sm text-muted-foreground mb-2">
                To reschedule or cancel, please email:
              </p>
              <a 
                href="mailto:therapywithanvitasethi@gmail.com"
                className="text-accent font-medium hover:underline"
              >
                therapywithanvitasethi@gmail.com
              </a>
            </div>

            <div className="text-center">
              <Button
                onClick={() => window.location.href = '/'}
                className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-6 text-lg"
                data-testid="go-to-home-button"
              >
                Back to Home
              </Button>
            </div>

            {/* Crisis Disclaimer */}
            <div className="mt-8">
              <CrisisDisclaimer variant="compact" />
            </div>
          </Card>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default BookSession;
